import { config } from "@/shared/lib/config";
import {
  formatDateForInput,
  formatTime24,
  toISOForAPI,
} from "@/shared/lib/date-time";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteMultiCombobox } from "@/shared/ui/InfiniteMultiCombobox";
import type { BookingSlot } from "@/shared/ui/timetable";
import {
  Button,
  confirm,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FormInput,
  FormRoot,
  FormTextarea,
  Input,
  Label,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useCallback, useEffect } from "react";
import { z } from "zod";
import type { CalendarEventDto } from "../../domain/types";
import {
  useCreateCalendarEvent,
  useDeleteCalendarEvent,
  useUpdateCalendarEvent,
} from "../api/queries";

const CalendarEventFormSchema = z
  .object({
    title: z.string().min(1, "ກະລຸນາໃສ່ຫົວຂໍ້"),
    description: z.string().optional(),
    date: z.string().min(1, "ກະລຸນາໃສ່ວັນ"),
    startTime: z.string().min(1, "ກະລຸນາໃສ່ເວລາເລີ່ມ"),
    endTime: z.string().optional(),
    attendeeIds: z.array(z.string()),
  })
  .refine(
    (data) => {
      const end = data.endTime?.trim();
      if (!end) return true;
      return end >= data.startTime;
    },
    { message: "ເວລາສິ້ນສຸດຕ້ອງບໍ່ກ່ອນເວລາເລີ່ມ", path: ["endTime"] },
  );

type CalendarEventFormValues = z.infer<typeof CalendarEventFormSchema>;

function localDateTimeToISO(date: string, time: string): string | undefined {
  const t = time.trim();
  if (!date || !t) return undefined;
  return toISOForAPI(`${date}T${t}:00`);
}

type CalendarEventFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSlot: BookingSlot | null;
  initialEvent: CalendarEventDto | null;
  defaultOwnerId: string;
  onSuccess?: () => void;
};

export function CalendarEventFormDialog({
  open,
  onOpenChange,
  initialSlot,
  initialEvent,
  defaultOwnerId,
  onSuccess,
}: CalendarEventFormDialogProps) {
  const isEdit = !!initialEvent;

  const methods = RHF.useForm<CalendarEventFormValues>({
    defaultValues: {
      title: "",
      description: "",
      date: "",
      startTime: "09:00",
      endTime: "10:00",
      attendeeIds: [],
    },
    resolver: zodResolver(CalendarEventFormSchema),
  });

  const createMutation = useCreateCalendarEvent();
  const updateMutation = useUpdateCalendarEvent();
  const deleteMutation = useDeleteCalendarEvent();

  const preloadAttendees = useCallback(
    async (ids: string[]): Promise<LookupItem[]> => {
      const url = `${config.apiUrl}/users/lookup`;
      const arr = await Promise.all(
        ids.map((id) => hydrateLookupItem(url, id)),
      );
      return arr.filter((x): x is LookupItem => x != null);
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    if (initialEvent) {
      methods.reset({
        title: initialEvent.title,
        description: initialEvent.description ?? "",
        date: formatDateForInput(initialEvent.startAt),
        startTime: formatTime24(initialEvent.startAt),
        endTime: initialEvent.endAt
          ? formatTime24(initialEvent.endAt)
          : "",
        attendeeIds: initialEvent.attendeeIds ?? [],
      });
    } else if (initialSlot) {
      methods.reset({
        title: "",
        description: "",
        date: initialSlot.date,
        startTime: initialSlot.startTime?.slice(0, 5) ?? "09:00",
        endTime: initialSlot.endTime?.slice(0, 5) ?? "10:00",
        attendeeIds: [],
      });
    } else {
      methods.reset({
        title: "",
        description: "",
        date: formatDateForInput(new Date()),
        startTime: "09:00",
        endTime: "10:00",
        attendeeIds: [],
      });
    }
  }, [open, initialEvent, initialSlot, methods]);

  const onSubmit = async (values: CalendarEventFormValues) => {
    const startAt = localDateTimeToISO(values.date, values.startTime);
    if (!startAt) {
      toast.error("ກະລຸນາກວດສອບວັນ ແລະ ເວລາເລີ່ມ");
      return;
    }
    const endTrim = values.endTime?.trim();
    const endAt = endTrim
      ? localDateTimeToISO(values.date, endTrim) ?? null
      : null;

    try {
      if (isEdit && initialEvent) {
        await updateMutation.mutateAsync({
          id: initialEvent.id,
          body: {
            title: values.title.trim(),
            description: values.description?.trim() || null,
            startAt,
            endAt,
            remindBy: initialEvent.remindBy ?? null,
            attendeeIds: values.attendeeIds,
          },
        });
        toast.success("ບັນທຶກກິດຈະກຳແລ້ວ");
      } else {
        await createMutation.mutateAsync({
          title: values.title.trim(),
          description: values.description?.trim() || null,
          startAt,
          endAt,
          ownerId: defaultOwnerId,
          remindBy: null,
          attendeeIds: values.attendeeIds,
        });
        toast.success("ສ້າງກິດຈະກຳແລ້ວ");
      }
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ເກີດຂໍ້ຜິດພາດ");
    }
  };

  const handleDelete = useCallback(async () => {
    if (!initialEvent) return;
    const ok = await confirm({
      title: "ລຶບກິດຈະກຳ?",
      description: `"${initialEvent.title}" ຈະຖືກລຶບອອກຖາວອນ. ການກະທຳນີ້ບໍ່ສາມາດກູ້ຄືນໄດ້.`,
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await deleteMutation.mutateAsync(initialEvent.id);
      toast.success("ລຶບກິດຈະກຳແລ້ວ");
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ເກີດຂໍ້ຜິດພາດ");
    }
  }, [initialEvent, deleteMutation, onOpenChange, onSuccess]);

  const loading =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "ແກ້ໄຂກິດຈະກຳ" : "ເພີ່ມກິດຈະກຳ"}</DialogTitle>
        </DialogHeader>
        <FormRoot<CalendarEventFormValues>
          methods={methods}
          onSubmit={onSubmit}
          className="space-y-4"
        >
          <FormInput
            name="title"
            label="ຫົວຂໍ້"
            placeholder="ຫົວຂໍ້ກິດຈະກຳ"
            requiredMark
          />
          <FormTextarea
            name="description"
            label="ຄຳອະທິບາຍ"
            placeholder="ຄຳອະທິບາຍ (ບໍ່ບັງຄັບ)"
          />
          <div className="grid grid-cols-2 gap-4">
            <FormInput name="date" label="ວັນ" type="date" />
            <Field name="startTime" label="ເວລາເລີ່ມ">
              <RHF.Controller
                control={methods.control}
                name="startTime"
                render={({ field }) => (
                  <Input
                    id="cal-start"
                    type="time"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    ref={field.ref}
                  />
                )}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field name="endTime" label="ເວລາສິ້ນສຸດ">
              <RHF.Controller
                control={methods.control}
                name="endTime"
                render={({ field }) => (
                  <Input
                    id="cal-end"
                    type="time"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    ref={field.ref}
                  />
                )}
              />
            </Field>
          </div>
          <div className="grid gap-2">
            <Label>ຜູ້ກ່ຽວຂ້ອງ</Label>
            <RHF.Controller
              control={methods.control}
              name="attendeeIds"
              render={({ field }) => (
                <InfiniteMultiCombobox<LookupItem>
                  queryKey={["management", "calendar", "attendees"]}
                  queryFn={(args) =>
                    fetchLookupForInfinite(`${config.apiUrl}/users/lookup`, args)
                  }
                  preloadQueryFn={preloadAttendees}
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  values={field.value}
                  onValuesChange={field.onChange}
                  placeholder="ເລືອກຜູ້ກ່ຽວຂ້ອງ"
                  clearable
                />
              )}
            />
          </div>
          <DialogFooter className="flex-wrap gap-2">
            {isEdit && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={loading}
                className="mr-auto"
              >
                ລຶບ
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
